import { groupBy, isEmpty, map, sum } from "lodash/fp";

import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import * as Filters from "@app/analytics/meetops/filters";
import { MeetOpsQuery } from "@app/analytics/meetops/types";
import { User } from "@app/common/types";
import { EnterpriseTeam } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { parseUTCDateTimeFromString } from "@app/utils/utils";

type TeamMetricMap = { [key: string]: { percent: number; time: number } };

export interface UseMeetingOverage {
  isInitialLoading: boolean;
  isError: boolean;
  byTeam: TeamMetricMap;
  average: { percent: number; time: number };
  refetch: () => void;
}

const getTimeAndPercentValues = (overages: number[]) => {
  // give 1 minute breathing time before a meeting is considered to have "run over"
  const validOverages = overages.filter((o) => o > 1);

  return {
    percent: (validOverages.length / overages.length) * 100,
    time: sum(validOverages) / overages.length,
  };
};

const getMeetingOverage = (data: any) => {
  const scheduledStartTime = parseUTCDateTimeFromString(
    data.scheduledStartTime
  );
  const scheduledEndTime = scheduledStartTime.plus({
    seconds: data.scheduledDuration,
  });

  const observedStartTime = parseUTCDateTimeFromString(data.observedStartTime);
  const observedEndTime = observedStartTime.plus({
    seconds: data.observedDuration,
  });

  const overageMs = observedEndTime.diff(scheduledEndTime).toMillis();

  // return overage in mins
  return Math.max(0, overageMs / 1000 / 60);
};

const getAverageOverageForTeams = (data: any) => {
  if (isEmpty(data)) return {};

  const sessionsByTeam = groupBy("teamId", data);
  const overageByTeam: TeamMetricMap = {};
  return Object.entries(sessionsByTeam).reduce((obj, [teamId, sessions]) => {
    const meetingOverages = map(getMeetingOverage, sessions);
    obj[teamId] = getTimeAndPercentValues(meetingOverages);
    return obj;
  }, overageByTeam);
};

const getAverageOverage = (data: any) => {
  if (isEmpty(data)) {
    return { percent: 0, time: 0 };
  }

  const meetingOverages = map(getMeetingOverage, data);
  return getTimeAndPercentValues(meetingOverages);
};

const useMeetingOverage = ({
  dateRanges,
  userId,
  teamId,
  includeTeamBreakout = false,
}: {
  dateRanges: MeetOpsQuery["dateRanges"];
  userId?: User["id"];
  teamId?: EnterpriseTeam["id"];
  includeTeamBreakout?: boolean;
}): UseMeetingOverage => {
  const { workspaceId } = useWorkspace();

  const teamsQuery = useMetricsQuery(
    {
      dateRanges,
      dimensions: [
        "teamId",
        "meetingId",
        "scheduledStartTime",
        "observedStartTime",
        "scheduledDuration",
        "observedDuration",
      ],
      metrics: [],
      filters: [Filters.scheduledMeetings()],
      people: "everyone",
    },
    {
      enabled: includeTeamBreakout,
      queryKey: ["meetingOverageByTeam", workspaceId],
      staleTime: 30 * 60 * 1000,
    }
  );

  const averageQuery = useMetricsQuery(
    {
      dateRanges,
      dimensions: [
        "meetingId",
        "scheduledStartTime",
        "observedStartTime",
        "scheduledDuration",
        "observedDuration",
      ],
      metrics: [],
      filters: [Filters.scheduledMeetings()],
      people: userId ? "only_me" : "everyone",
      userId,
      teamId,
    },
    {
      queryKey: ["averageMeetingOverage", workspaceId, userId, teamId],
      staleTime: 30 * 60 * 1000,
    }
  );

  const teamsData = teamsQuery.data?.results[0]?.data;
  const workspaceData = averageQuery.data?.results[0]?.data;

  const refetchAll = () => {
    if (teamsQuery.isError) teamsQuery.refetch();
    if (averageQuery.isError) averageQuery.refetch();
  };

  return {
    isInitialLoading:
      teamsQuery.isInitialLoading || averageQuery.isInitialLoading,
    isError: teamsQuery.isError || averageQuery.isError,
    byTeam: getAverageOverageForTeams(teamsData),
    average: getAverageOverage(workspaceData),
    refetch: refetchAll,
  };
};

export default useMeetingOverage;
